import React, { Fragment, ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { FormErrorMessage } from 'src/components/shared/form/FormErrorMessage'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { QuestionMarkMajor } from '@shopify/polaris-icons'
import { getCurrentCollectDataStep } from 'src/selectors/collectData'
import { CollectDataFormItem } from 'src/components/collect-data-form/CollectDataFormItem'
import { Formik } from 'formik'
import { ClientSecureWrapper } from 'src/components/layout/client/ClientSecureWrapper'
import { getValidationSchema } from './CollectDataForm.validator'
import { CollectDataFormItemType } from 'src/constants/collect-data/enums'
import { CollectDataFormInputItem, UploadFormResponse } from 'src/interfaces/collectDataForm'
import { getFormUploadValue } from './CollectDataForm.utils'
import { SkipStep, UploadForm } from 'src/constants/actionTypes'
import { FailedAttemptModal } from './FailedAttemptModal'
import { FormStep } from 'src/interfaces/collectData'
import { Button } from '../shared/Button'
import { useStepFormStorage } from 'src/hooks/useStepFormStorage'
import { BasicRadioGroup } from '../shared/form/BasicRadioGroup'
import { StepLabels } from '../client/step-labels/StepLabels'
import { replaceTrainingWording } from 'src/utils/certificationWording'

const showFormValues = [
  {
    label: 'I want to fill out this form',
    value: 'yes',
  },
  {
    label: 'Continue without completing the form',
    value: 'no',
  },
]

export const CollectDataForm = () => {
  const dispatch = useDispatch()

  const currentStep = useSelector(getCurrentCollectDataStep) as FormStep

  const [showForm, setShowForm] = useState<boolean | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isFailedAttemptModalOpen, setIsFailedAttemptModalOpen] = useState<boolean>(false)
  const [attemptsLeft, setAttemptsLeft] = useState<number>(0)
  const [error, setError] = useState<string | null>(null)

  const formVilidatableItems = currentStep.metadata.formItems.filter(
    (item) =>
      item.type !== CollectDataFormItemType.TextBlock &&
      item.type !== CollectDataFormItemType.Video,
  ) as CollectDataFormInputItem[]

  const fileFieldIds = useMemo(
    () =>
      formVilidatableItems
        .filter((item) => {
          return item.type === CollectDataFormItemType.File
        })
        .map((item) => {
          return item.id
        }),
    [formVilidatableItems],
  )

  const { savedValues, onValuesChange, clearStorage } = useStepFormStorage<Record<string, unknown>>(
    {
      storageKey: `step_form_${currentStep?.id}`,
      fileFields: fileFieldIds,
    },
  )

  useEffect(() => {
    if (!currentStep?.optional) {
      setShowForm(true)
    } else {
      setShowForm(null)
    }
  }, [currentStep?.id, currentStep?.optional])

  const handleChangeShowForm = useCallback((event: ChangeEvent<{ value: string }>) => {
    setShowForm(event.target.value === 'yes')
  }, [])

  const openFailedAttemptModal = useCallback(() => {
    setIsFailedAttemptModalOpen(true)
  }, [])

  const closeFailedAttemptModal = useCallback(() => {
    setIsFailedAttemptModalOpen(false)
  }, [])

  const handleFormSubmit = async (values: any) => {
    setIsLoading(true)
    setError(null)

    try {
      if (showForm === false) {
        await createAsyncAction(dispatch, SkipStep.request({ stepId: currentStep.id }))
        clearStorage()
      } else {
        const data = formVilidatableItems
          .map((item) => ({
            id: item.id,
            type: item.type,
            title: item.title,
            value: getFormUploadValue(item, values[item.id]),
          }))
          .filter((item) => !!item.value)

        const requestBody = {
          data: data.filter((item) => item.type !== CollectDataFormItemType.File),
          files: data.filter((item) => item.type === CollectDataFormItemType.File),
          stepId: currentStep.id,
        }

        const response: UploadFormResponse = await createAsyncAction(
          dispatch,
          UploadForm.request(requestBody),
        )

        if (
          response?.code === 'attempt_failed' &&
          response?.collectDataStep?.maxAttempts + 1 - response?.collectDataStep?.failedAttempts > 0
        ) {
          setAttemptsLeft(
            response?.collectDataStep?.maxAttempts + 1 - response?.collectDataStep?.failedAttempts,
          )
          openFailedAttemptModal()
        } else {
          clearStorage()
        }
      }
    } catch (err: any) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  if (!currentStep) {
    return null
  }

  const defaultFormValues = formVilidatableItems.reduce((acc: any, item) => {
    acc[item.id] = item.type === CollectDataFormItemType.Checkbox ? [] : ''

    return acc
  }, {})

  const formInitialValues = savedValues ?? defaultFormValues

  return (
    <Formik
      initialValues={formInitialValues}
      enableReinitialize
      onSubmit={handleFormSubmit}
      validationSchema={showForm === true ? getValidationSchema(formVilidatableItems) : undefined}
    >
      {({ handleSubmit, values: formValues }) => {
        onValuesChange(formValues)

        return (
          <ClientSecureWrapper
            footer={
              <Fragment>
                <FormErrorMessage css={tw`mb-4`} error={error} />
                <Button
                  className={tw`w-full mt-2`}
                  loading={isLoading}
                  disabled={currentStep.optional && showForm === null}
                  onClick={() => {
                    handleSubmit()
                  }}
                  data-testid="contractors-onboarding-form-submit-btn"
                >
                  Submit and continue
                </Button>
              </Fragment>
            }
          >
            <div
              css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mt-2 mb-4`}
            >
              <QuestionMarkMajor width={20} />
            </div>
            <StepLabels />
            <Typography css={tw`my-2`} variant="h2">
              {replaceTrainingWording(currentStep.metadata.title ?? '') || 'Questionnaire'}
            </Typography>
            <Typography css={tw`mb-4 text-shades-400`}>
              {replaceTrainingWording(currentStep.metadata.description ?? '') ||
                "Let's see if you're a good fit."}
            </Typography>
            {currentStep?.optional && (
              <Fragment>
                <Typography css={tw`mb-2 mt-6 text-shades-400`} variant="overline">
                  Select an option
                </Typography>
                <div css={tw`bg-shades-0 p-4 rounded-2xl mb-8`}>
                  <BasicRadioGroup
                    options={showFormValues}
                    onChange={handleChangeShowForm}
                    dataTestId="contractors-onboarding-ein-business-option-input"
                  />
                </div>
              </Fragment>
            )}
            {(!currentStep.optional || showForm) && (
              <div css={tw`mt-6 bg-shades-0 p-4 rounded-2xl mb-8`}>
                {currentStep.metadata.formItems.map((item) => (
                  <CollectDataFormItem key={item.id} collectDataFormItem={item} />
                ))}
              </div>
            )}
            <FailedAttemptModal
              attemptsLeft={attemptsLeft}
              isOpen={isFailedAttemptModalOpen}
              onClose={closeFailedAttemptModal}
            />
          </ClientSecureWrapper>
        )
      }}
    </Formik>
  )
}
